import React, {
	useState,
	useEffect,
	useRef,
	HTMLAttributes,
	Fragment,
} from "react";
import { XMarkIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { debounce } from "lodash";
import clsx from "clsx";
import {
	ChevronUpIcon,
	ChevronDownIcon,
	CheckIcon,
} from "@heroicons/react/20/solid";
import {
	Combobox,
	Transition,
	ComboboxButton,
	ComboboxInput,
	ComboboxOption,
	ComboboxOptions,
} from "@headlessui/react";

type Placement = "top" | "bottom" | "left" | "right";
type Align = "start" | "end";

interface CustomAutoCompleteProps extends HTMLAttributes<HTMLInputElement> {
	className?: string;
	fetchData: (query: string) => Promise<Record<string, any>[]>;
	onSelect: (data: any) => void;
	label?: string;
	labelclassname?: string;
	containerClassName?: string;
	errors: Array<{
		isValidated: boolean;
		message: string;
	}>;
	errorclassname?: string;
	showerrors: boolean;
	dataDisplay: (data: Record<string, any> | null) => string;
	value: Record<string, any> | null;
	labelNoResult: string;
	refreshKey?: any;
	placeholder?: string;
	anchor?: `${Placement} ${Align}`;
}

const CustomAutoComplete: React.FC<CustomAutoCompleteProps> = ({
	className,
	fetchData,
	onSelect,
	showerrors,
	errors,
	errorclassname,
	value,
	dataDisplay,
	labelNoResult,
	containerClassName,
	refreshKey,
	placeholder,
	anchor,
	...props
}) => {
	const [inputValue, setInputValue] = useState(dataDisplay(value));
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState<Record<string, any>[]>([]);
	const [selectedItem, setSelectedItem] = useState<any>(value || null);
	const prevQueryRef = useRef<string | null>(null);

	useEffect(() => {
		setInputValue(dataDisplay(value));
		setSelectedItem(value);
	}, [value]);

	const fetchDataRef = useRef(fetchData);
	useEffect(() => {
		fetchDataRef.current = fetchData;
	}, [fetchData]);

	const debouncedFetchData = useRef(
		debounce(async (searchQuery: string) => {
			if (searchQuery !== prevQueryRef.current) {
				prevQueryRef.current = searchQuery;
				setLoading(true);
				try {
					const result = await fetchDataRef.current(searchQuery);
					setData(result);
				} catch (error) {
					console.error("Error fetching data:", error);
				} finally {
					setLoading(false);
				}
			}
		}, 1000),
	).current;

	useEffect(() => {
		return () => {
			debouncedFetchData.cancel();
		};
	}, [debouncedFetchData]);

	useEffect(() => {
		if (refreshKey !== undefined) {
			prevQueryRef.current = null;
		}
		debouncedFetchData(inputValue);
	}, [inputValue, refreshKey, debouncedFetchData]);

	const handleClear = () => {
		setInputValue("");
		setSelectedItem(null);
		setData([]);
		onSelect(null);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;
		setInputValue(newValue);
		setSelectedItem(null);
	};

	const handleSelectItem = (item: Record<string, any>) => {
		setSelectedItem(item);
		const display = dataDisplay(item);
		setInputValue(display);
		onSelect(item);
	};

	return (
		<div className={clsx("relative", containerClassName)}>
			<Combobox value={selectedItem} onChange={handleSelectItem}>
				{({ open }) => (
					<div className="relative">
						{loading && (
							<ArrowPathIcon className="absolute inset-y-0 right-10 h-full w-6 animate-spin text-secondary" />
						)}
						{!loading && inputValue && (
							<XMarkIcon
								className="absolute inset-y-0 right-10 h-full w-6 cursor-pointer text-secondary z-10"
								onClick={handleClear}
							/>
						)}
						<ComboboxInput
							{...props}
							className={clsx("focus:border-none pl-2 pr-10", className)}
							onChange={handleInputChange}
							placeholder={placeholder || ""}
							value={inputValue}
						/>
						<ComboboxButton className="absolute inset-y-0 right-2 flex items-center pr-2 bg-none">
							{open ? (
								<ChevronUpIcon className="h-5 w-5 text-black" />
							) : (
								<ChevronDownIcon className="h-5 w-5 text-black" />
							)}
						</ComboboxButton>
						<Transition
							as={Fragment}
							leave="transition ease-in duration-100"
							leaveFrom="opacity-100"
							leaveTo="opacity-0"
						>
							<ComboboxOptions
								anchor={anchor || "bottom start"}
								className={clsx(
									"w-[var(--input-width)]",
									"z-10 mt-1 max-h-60 rounded-md bg-white py-1 text-base shadow-lg ring-1",
									"ring-black ring-opacity-5 focus:outline-none sm:text-sm",
								)}
							>
								{data.length === 0 && inputValue !== "" ? (
									<div className="relative cursor-default select-none py-2 px-4 text-gray-700">
										{labelNoResult || "Nenhum resultado encontrado"}
									</div>
								) : (
									data.map((item, index) => (
										<ComboboxOption
											key={index}
											className={({ focus }) =>
												clsx(
													(className =
														"relative cursor-default select-none py-2 pl-10 pr-4"),
													focus
														? "bg-primary text-white"
														: "bg-white text-primary",
												)
											}
											value={item}
										>
											{({ focus, selected }) => (
												<>
													<span
														className={clsx(
															"block truncate",
															selected ? "font-medium" : "font-normal",
														)}
													>
														{dataDisplay(item)}
													</span>
													{(selected ||
														dataDisplay(item) ===
															dataDisplay(selectedItem)) && (
														<span
															className={clsx(
																"absolute inset-y-0 left-0 flex items-center pl-3",
																focus ? "text-white" : "text-primary",
															)}
														>
															<CheckIcon
																className="h-5 w-5"
																aria-hidden="true"
															/>
														</span>
													)}
												</>
											)}
										</ComboboxOption>
									))
								)}
							</ComboboxOptions>
						</Transition>
					</div>
				)}
			</Combobox>
			{showerrors &&
				errors.filter((error) => !error.isValidated).length > 0 && (
					<ul role="list" className="list-disc space-y-1 pl-5">
						{errors
							.filter((error) => !error.isValidated)
							.map((error, index) => (
								<li key={index} className={errorclassname || "text-red-600"}>
									{error.message}
								</li>
							))}
					</ul>
				)}
		</div>
	);
};

export default CustomAutoComplete;
